import { z } from 'zod';

/**
 * US States enum for address validation
 */
export const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'DC',
  'PR',
  'VI',
  'GU',
  'AS',
  'MP',
] as const;

export type USState = (typeof US_STATES)[number];

/**
 * Supported countries for shipping
 */
export const SUPPORTED_COUNTRIES = ['US', 'CA', 'GB'] as const;
export type SupportedCountry = (typeof SUPPORTED_COUNTRIES)[number];

export const COUNTRY_OPTIONS: { value: SupportedCountry; label: string }[] = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
];

/**
 * Address Form Schema
 * Validates shipping/billing address information
 * Field names match the SDK's CreateUserVerifiedAddressRequest type
 */
export const addressSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(50, 'First name must be less than 50 characters'),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .max(50, 'Last name must be less than 50 characters'),
  street: z
    .string()
    .min(1, 'Street address is required')
    .max(100, 'Address must be less than 100 characters'),
  apartmentNumber: z
    .string()
    .max(100, 'Apartment number must be less than 100 characters')
    .optional()
    .or(z.literal('')),
  city: z.string().min(1, 'City is required').max(50, 'City must be less than 50 characters'),
  subdivision: z
    .string()
    .min(1, 'State/Province is required')
    .max(50, 'State/Province must be less than 50 characters'),
  postalCode: z
    .string()
    .min(1, 'Postal code is required')
    .max(20, 'Postal code must be less than 20 characters'),
  countryCode: z.enum(SUPPORTED_COUNTRIES, {
    message: 'Please select a valid country',
  }),
  phoneNumber: z
    .string()
    .min(1, 'Phone number is required')
    .refine((val) => /^\+?[\d\s().-]{10,20}$/.test(val), 'Please enter a valid phone number'),
});

export type AddressFormData = z.infer<typeof addressSchema>;

/**
 * US-specific address schema with stricter validation
 */
export const usAddressSchema = addressSchema.extend({
  subdivision: z.enum(US_STATES, {
    message: 'Please select a valid US state',
  }),
  postalCode: z
    .string()
    .min(1, 'ZIP code is required')
    .regex(/^\d{5}(-\d{4})?$/, 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)'),
});

export type USAddressFormData = z.infer<typeof usAddressSchema>;

/**
 * Partial Address Schema for updates
 * All fields optional for PATCH operations
 */
export const partialAddressSchema = addressSchema.partial();

export type PartialAddressFormData = z.infer<typeof partialAddressSchema>;
